import { Coffee, MapPin, Phone, Clock, Instagram, MessageCircle, Facebook } from "lucide-react";
import { Link } from "react-router-dom";

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M16.5 3a5.6 5.6 0 0 0 4.5 4.5v3a8.6 8.6 0 0 1-4.5-1.2v6.2a6.5 6.5 0 1 1-6.5-6.5c.34 0 .67.03 1 .08v3.1a3.5 3.5 0 1 0 2.5 3.36V3h3z" />
  </svg>
);

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 1.67c2.2 0 4.27.86 5.83 2.41a8.2 8.2 0 0 1 2.42 5.83c0 4.54-3.7 8.24-8.25 8.24a8.2 8.2 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24zm-3.6 4.43c-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1 0 1.24.9 2.43 1.03 2.6.13.16 1.76 2.69 4.27 3.77.6.26 1.06.41 1.42.53.6.19 1.14.16 1.57.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29-.25-.12-1.47-.72-1.7-.81-.23-.08-.39-.12-.56.13-.16.25-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.13-1.05-.39-2-1.23-.74-.66-1.24-1.48-1.38-1.73-.15-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.08-.17.04-.31-.02-.44-.06-.12-.56-1.35-.77-1.85-.2-.48-.4-.42-.56-.43h-.47z" />
  </svg>
);

const ModernFooter = () => {
  return (
    <footer className="bg-muted/30 border-t">
      <div className="container max-w-screen-xl py-12">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Coffee className="h-6 w-6 text-primary" />
              <h3 className="text-lg font-semibold">La Dalia Café</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Tu café de especialidad favorito. Cada taza cuenta, cada visita te acerca a más recompensas.
            </p>
            <div className="space-y-3">
              <p className="text-sm font-semibold">Síguenos en nuestras redes sociales</p>
              <div className="flex flex-col gap-2">
                <a
                  href="https://www.instagram.com/ladaliacafe/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  <Instagram className="h-5 w-5" />
                  <span>Instagram</span>
                </a>
                <a
                  href="https://www.tiktok.com/@ladaliacafe"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  <TikTokIcon className="h-5 w-5" />
                  <span>TikTok</span>
                </a>
                <a
                  href="https://www.facebook.com/LaDaliaCafe"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  <Facebook className="h-5 w-5" />
                  <span>Facebook</span>
                </a>
                <a
                  href="https://whatsapp.com/channel/0029Va5duNn0AgWHvQc7xx2n"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Canal de WhatsApp"
                  className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  <WhatsAppIcon className="h-5 w-5" />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Información</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <a
                href="https://maps.app.goo.gl/WABhgkkxR2e6KbKz9"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ver ubicación en Google Maps"
                className="flex items-start space-x-2 hover:text-primary transition-colors"
              >
                <MapPin className="h-5 w-5 flex-shrink-0" />
                <span>Francisco Morazán (av. 8) esq. Calle 29, Col. Valentín Gómez Farías, Venustiano Carranza, CDMX</span>
              </a>
              <div className="flex items-start space-x-2">
                <Phone className="h-5 w-5 flex-shrink-0" />
                <span>55 1543 9796</span>
              </div>
              <div className="flex items-start space-x-2">
                <MessageCircle className="h-5 w-5 flex-shrink-0" />
                <span>55 4897 3046</span>
              </div>
              <div className="flex items-start space-x-2">
                <Clock className="h-5 w-5 flex-shrink-0" />
                <span>Lunes 4:00pm a 10:00pm<br />Martes a S&aacute;bado 11:30am - 10:00pm<br />Domingo 4:00pm - 8:30pm</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Menú</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <span className="block hover:text-primary transition-colors cursor-pointer">Cafés Especiales</span>
              <span className="block hover:text-primary transition-colors cursor-pointer">Bebidas Frías</span>
              <span className="block hover:text-primary transition-colors cursor-pointer">Pasteles & Postres</span>
              <span className="block hover:text-primary transition-colors cursor-pointer">Desayunos</span>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Programa de Lealtad</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <Link to="/myPoints" className="block hover:text-primary transition-colors">Consultar Puntos</Link>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
          <p>&copy; 2024 La Dalia Café. Todos los derechos reservados.</p>
        </div>
      </div>
    </footer>
  );
};

export default ModernFooter;